import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart3, Users, CheckCircle, XCircle, Clock, Calendar, 
  TrendingUp, Briefcase, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const JobApplicationAnalytics = ({ getAuthHeader }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/forms/job-applications/analytics`, getAuthHeader());
      setAnalytics(response.data);
    } catch (error) {
      console.error('Analytics fetch error:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (getAuthHeader) {
      fetchAnalytics();
    }
  }, []);

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/forms/job-applications/send-reminders`, {}, getAuthHeader());
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12 text-slate-500">
        Failed to load analytics. Please try again.
      </div>
    );
  }

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700 border-amber-200',
    approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-100 text-red-700 border-red-200',
    under_review: 'bg-blue-100 text-blue-700 border-blue-200'
  };

  const positionColors = [
    'bg-blue-500',
    'bg-emerald-500',
    'bg-purple-500',
    'bg-amber-500',
    'bg-rose-500'
  ];

  const maxPositionCount = Math.max(...Object.values(analytics.position_breakdown));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">Application Analytics</h2>
            <p className="text-slate-500 text-sm">Overview of job applications</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAnalytics}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button 
            size="sm" 
            className="bg-purple-600 hover:bg-purple-700"
            onClick={handleSendReminders}
            disabled={sendingReminders}
          >
            <Calendar className="w-4 h-4 mr-2" />
            {sendingReminders ? 'Sending...' : 'Send Interview Reminders'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{analytics.total_applications}</p>
              <p className="text-xs text-blue-600">Total Applications</p>
            </div>
          </div>
        </div>

        <div className="card p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{analytics.pending_review}</p>
              <p className="text-xs text-amber-600">Pending Review</p>
            </div>
          </div>
        </div>

        <div className="card p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-700">{analytics.conversion_rate}%</p>
              <p className="text-xs text-emerald-600">Approval Rate</p>
            </div>
          </div>
        </div>

        <div className="card p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-700">{analytics.interviews_scheduled}</p>
              <p className="text-xs text-purple-600">Interviews Scheduled</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <div className="card p-5">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            Status Breakdown
          </h3>
          <div className="space-y-3">
            {Object.entries(analytics.status_breakdown).map(([status, count]) => {
              const percentage = analytics.total_applications > 0 
                ? (count / analytics.total_applications * 100).toFixed(1) 
                : 0;
              const colorClass = statusColors[status] || 'bg-slate-100 text-slate-700';
              
              return (
                <div key={status} className="flex items-center gap-3">
                  <div className="w-24 text-sm capitalize">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}>
                      {status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${status === 'approved' ? 'bg-emerald-500' : status === 'rejected' ? 'bg-red-500' : status === 'under_review' ? 'bg-blue-500' : 'bg-amber-500'} transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="w-16 text-right">
                    <span className="font-semibold text-slate-700">{count}</span>
                    <span className="text-slate-400 text-xs ml-1">({percentage}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Position Breakdown */}
        <div className="card p-5">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-blue-600" />
            Applications by Position
          </h3>
          <div className="space-y-3">
            {Object.entries(analytics.position_breakdown)
              .sort((a, b) => b[1] - a[1])
              .map(([position, count], index) => {
                const percentage = maxPositionCount > 0 
                  ? (count / maxPositionCount * 100) 
                  : 0;
                
                return (
                  <div key={position} className="flex items-center gap-3">
                    <div className="w-36 text-sm truncate text-slate-700">{position}</div>
                    <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${positionColors[index % positionColors.length]} transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="w-12 text-right font-semibold text-slate-700">{count}</div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Monthly Trend */}
      <div className="card p-5">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-emerald-600" />
          Monthly Application Trend
        </h3>
        {analytics.monthly_trend.length > 0 ? (
          <div className="flex items-end gap-2 h-48">
            {analytics.monthly_trend.map((item, index) => {
              const maxCount = Math.max(...analytics.monthly_trend.map(t => t.count));
              const heightPercent = maxCount > 0 ? (item.count / maxCount * 100) : 0;
              const [year, month] = item.month.split('-');
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              const monthLabel = monthNames[parseInt(month) - 1];
              
              return (
                <div key={item.month} className="flex-1 flex flex-col items-center gap-2">
                  <div className="w-full flex flex-col items-center justify-end h-36">
                    <span className="text-xs font-semibold text-slate-700 mb-1">{item.count}</span>
                    <div 
                      className="w-full max-w-12 bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t-lg transition-all duration-500 hover:from-emerald-600 hover:to-emerald-500"
                      style={{ height: `${Math.max(heightPercent, 5)}%` }}
                    />
                  </div>
                  <div className="text-xs text-slate-500 text-center">
                    <div>{monthLabel}</div>
                    <div className="text-slate-400">{year}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            No trend data available yet
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="card p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-800">Interview Reminders</h4>
            <p className="text-sm text-blue-700 mt-1">
              Reminder emails are automatically sent 24 hours before scheduled interviews. 
              You can also manually trigger reminders using the button above.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobApplicationAnalytics;
